import {Controller, useForm} from 'react-hook-form';
import React, {useState} from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import {Visibility, VisibilityOff} from '@material-ui/icons';

export type UpdatePasswordFormSubmission = {
  oldPassword: string;
  newPassword: string;
};

export function UpdatePasswordForm(props: {
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (submission: UpdatePasswordFormSubmission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
}) {
  type Submission = UpdatePasswordFormSubmission & {
    confirmNewPassword: string;
  };

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control, getValues} = useForm<Submission>();

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      oldPassword: submission.oldPassword,
      newPassword: submission.newPassword,
    });
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Controller
                name="oldPassword"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Old Password"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            onMouseDown={handleTogglePasswordVisibility}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Password required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="newPassword"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="New Password"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            onMouseDown={handleTogglePasswordVisibility}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'New password required',
                  minLength: {
                    value: 8,
                    message: 'Password should contain at least 8 characters',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="confirmNewPassword"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Confirm New Password"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            onMouseDown={handleTogglePasswordVisibility}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Confirm password required',
                  validate: (value) =>
                    value === getValues('newPassword') ||
                    'Passwords do not match',
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            style={{paddingTop: '1rem', paddingBottom: '1rem'}}
          >
            <Grid item xs={12} sm={6}>
              {props.cancelButtonTitle && (
                <Button
                  className={'secondary-btn'}
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={props.isLoading}
                  onClick={props.onCancel}
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                className={'primary-btn'}
                type="submit"
                variant="contained"
                disableElevation
                fullWidth
                color="primary"
                disabled={props.isLoading}
              >
                {props.isLoading ? (
                  <CircularProgress color={'primary'} size={24} thickness={6} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
